"use client"

import { useEffect, useState, type ReactNode } from "react"

import {
  browserAnalyticsConsentStorage,
  hasAnalyticsConsent,
  guardLoadingPostHog,
  reconcilePostHogConsent,
  subscribeToAnalyticsConsent,
} from "@/lib/analytics-consent"

declare global {
  interface Window {
    posthog: any
  }
}

interface PostHogProviderProps {
  children: ReactNode
}

const POSTHOG_READY_EVENT = "complyrx-posthog-ready"

function postHogIsReady(posthog: any): boolean {
  return posthog?.__loaded === true
}

function applyCurrentConsent(allowed: boolean, posthog = window.posthog): void {
  reconcilePostHogConsent(posthog, allowed, postHogIsReady(posthog))
}

export function PostHogProvider({ children }: PostHogProviderProps) {
  const [analyticsConsent, setAnalyticsConsent] = useState(false)

  useEffect(() => {
    const sync = (allowed: boolean) => {
      // Apply the SDK boundary before waiting for React to commit new state.
      applyCurrentConsent(allowed)
      setAnalyticsConsent(allowed)
    }
    const onReady = (event: Event) => {
      const posthog = (event as CustomEvent).detail ?? window.posthog
      const allowed = hasAnalyticsConsent()
      reconcilePostHogConsent(posthog, allowed, true)
      setAnalyticsConsent(allowed)
    }

    window.addEventListener(POSTHOG_READY_EVENT, onReady)
    const unsubscribe = subscribeToAnalyticsConsent(window, browserAnalyticsConsentStorage(), sync)
    sync(hasAnalyticsConsent())

    return () => {
      unsubscribe()
      window.removeEventListener(POSTHOG_READY_EVENT, onReady)
    }
  }, [])

  useEffect(() => {
    if (!analyticsConsent || !hasAnalyticsConsent()) return

    const existing = window.posthog
    if (postHogIsReady(existing)) {
      reconcilePostHogConsent(existing, true, true)
      return
    }

    // An existing loader owns initialisation. Its ready callback will reconcile
    // against the latest stored choice, including a withdrawal during loading.
    if (document.querySelector("script[data-posthog]")) return

    const posthogKey = process.env.NEXT_PUBLIC_POSTHOG_KEY
    if (!posthogKey?.trim()) {
      console.warn("[PostHog] PostHog API key not configured (NEXT_PUBLIC_POSTHOG_KEY missing). PostHog will not be initialized.")
      return
    }

    const script = document.createElement("script")
    script.setAttribute("data-posthog", "true")
    script.type = "text/javascript"

    const escapedKey = JSON.stringify(posthogKey)
    const escapedHost = JSON.stringify("/ingest")
    script.textContent = `!function(t,e){var o,n,p,r;e.__SV||(window.posthog=e,e._i=[],e.init=function(i,s,a){function g(t,e){var o=e.split(".");2==o.length&&(t=t[o[0]],e=o[1]);t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}}(p=t.createElement("script")).type="text/javascript",p.async=!0,p.src=s.api_host+"/static/array.js",(r=t.getElementsByTagName("script")[0]).parentNode.insertBefore(p,r);var u=e;for(void 0!==a?u=e[a]=[]:a="posthog",u.people=u.people||[],u.toString=function(t){var e="posthog";return"posthog"!==a&&(e+="."+a),t||(e+=" (stub)"),e},u.people.toString=function(){return u.toString(1)+".people (stub)"},o="capture identify alias people.set people.set_once set_config register register_once unregister opt_out_capturing has_opted_out_capturing opt_in_capturing reset isFeatureEnabled onFeatureFlags".split(" "),n=0;n<o.length;n++)g(u,o[n]);e._i.push([i,s,a])},e.__SV=1)}(document,window.posthog||[]);posthog.init(${escapedKey},{api_host:${escapedHost},ui_host:"https://eu.posthog.com",person_profiles:"identified_only",defaults:"2025-05-24",opt_out_capturing_by_default:true,loaded:function(posthog){window.dispatchEvent(new CustomEvent(${JSON.stringify(POSTHOG_READY_EVENT)},{detail:posthog}))}});`

    if (!document.head) {
      console.error("[PostHog] DOM not ready, cannot append script")
      return
    }

    try {
      document.head.appendChild(script)
      guardLoadingPostHog(window.posthog)
    } catch (error) {
      console.error("[PostHog] Failed to load script:", error)
    }
  }, [analyticsConsent])

  return <>{children}</>
}
